import type { WebsocketProvider } from 'y-websocket';
import type { Doc } from 'yjs';
import type { WebsocketAwarenessState } from './websocket';

import { useCallback, useEffect, useRef, useState } from 'react';

import { getEnv } from '../../lib/env';

import { useApiKeys } from '../api';
import useSelectedSpace from '../useSelectedSpace';
import { disconnectFromRoom, getWebSocketConnection } from './websocket';

type WsStatus =
  'idle' | 'connecting' | 'connected' | 'failed' | 'retrying' | 'synced';

const APIKEY_PARAMS = { name: 'read only api key' };
const RETRY_COUNT = 3;
const RETRY_TIMEOUT = 5000;

const useWebsocket = (roomId: string, shouldConnect: boolean) => {
  const { spaceFeatureFlags } = useSelectedSpace();

  const [wsStatus, setWsStatus] = useState<WsStatus>('connecting');
  const wsRetryCountRef = useRef(0);
  const attemptTimeout = useRef<NodeJS.Timeout>(undefined);

  const [yjsDocument, setYjsDocument] = useState<Doc>();
  const [ws, setWs] = useState<WebsocketProvider>();

  const [editors, setEditors] = useState<WebsocketAwarenessState[]>([]);

  const { data: apiKeysData, isLoading: apiKeysLoading } =
    useApiKeys(APIKEY_PARAMS);

  const onConnectionFail = useCallback((ws: WebsocketProvider) => {
    ws.disconnect();
    setWsStatus('failed');
    clearTimeout(attemptTimeout.current);
    wsRetryCountRef.current = 0;
  }, []);

  const startAttemptTimeout = useCallback(
    (ws: WebsocketProvider) => {
      clearTimeout(attemptTimeout.current);
      attemptTimeout.current = setTimeout(() => {
        onConnectionFail(ws);
      }, RETRY_TIMEOUT);
    },
    [onConnectionFail],
  );

  const connectWithWebsocket = useCallback(() => {
    if (apiKeysLoading) {
      setWsStatus('connecting');
      return;
    }

    const apiKey = apiKeysData?.[0]?.apiKey;
    if (!apiKey) {
      setWsStatus('failed');
      return;
    }

    const { ws, yjsDocument } = getWebSocketConnection(apiKey, roomId);

    setYjsDocument(yjsDocument);
    setWs(ws);

    startAttemptTimeout(ws);

    const statusHandler = ({ status }: { status: string }) => {
      if (status === 'connected') {
        wsRetryCountRef.current = 0;
        setWsStatus('connected');
        clearTimeout(attemptTimeout.current);
      } else if (['disconnected', 'connecting'].includes(status)) {
        wsRetryCountRef.current++;
        if (wsRetryCountRef.current >= RETRY_COUNT) {
          onConnectionFail(ws);
        }
      }
    };

    ws.on('status', statusHandler);
    ws.on('sync', (isSynced) => {
      if (isSynced) {
        setWsStatus('synced');
      }
    });
  }, [
    apiKeysData,
    apiKeysLoading,
    onConnectionFail,
    roomId,
    startAttemptTimeout,
  ]);

  useEffect(() => {
    if (
      !shouldConnect ||
      !getEnv('VITE_COLLABORATION_ENDPOINT') ||
      !spaceFeatureFlags?.formCollaboration
    ) {
      setWsStatus('idle');
      return;
    }

    const handleUnload = () => {
      disconnectFromRoom(roomId);
    };

    connectWithWebsocket();
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearTimeout(attemptTimeout.current);
      disconnectFromRoom(roomId);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [
    connectWithWebsocket,
    roomId,
    shouldConnect,
    spaceFeatureFlags?.formCollaboration,
  ]);

  const retryWsConnection = () => {
    if (!ws) return;
    setWsStatus('retrying');
    wsRetryCountRef.current = 0;
    startAttemptTimeout(ws);
    ws.connect();
  };

  useEffect(() => {
    if (!ws) return;
    ws.awareness.on('update', () => {
      const editors = Array.from(ws.awareness.getStates().entries())
        .filter(([clientId]) => clientId !== ws.awareness.clientID)
        .filter(([, state]) => state.userId)
        .map(([, state]) => state as WebsocketAwarenessState);
      setEditors(editors);
    });
  }, [ws]);

  return {
    yjsDocument,
    wsStatus,
    retryWsConnection,
    editors,
    ws,
    awareness: ws?.awareness,
  };
};

export default useWebsocket;
